import { useCallback, useEffect, useRef, useState } from "react";
import { ISubscription } from "../interfaces/subscription.interface";
import { useUnmount } from "./use-unmount.hook";
import { useIntesectionObserver } from "./use-intersection-observer.hook";
import { EventEmitter } from "../event-emitter";

export const useIntersection = (callback: () => boolean, enable = true) => {
	const intersectionObserverRef = useRef<HTMLDivElement | null>(null);

	const observer = useIntesectionObserver((_entries?: any, observer?: IntersectionObserver) => {
		let unobserve = true;
		if (callback) unobserve = callback();
		if (unobserve && intersectionObserverRef.current) observer?.unobserve(intersectionObserverRef.current);
	});

	const [scrollEventListenerAdded, setScrollEventListenerAdded] = useState(false);
	const scrollEventSubscriptionRef = useRef<ISubscription<"scroll"> | null>(null);
	const eventEmitterRef = useRef<EventEmitter<"scroll">>(new EventEmitter<"scroll">());
	const handler = useRef(() => eventEmitterRef.current.emit("scroll"));

	const removeEventListener = useCallback(() => {
		window.removeEventListener("touchmove", handler.current);
		window.removeEventListener("wheel", handler.current);
		window.removeEventListener("scroll", handler.current);
	}, []);

	const disable = useCallback(() => {
		removeEventListener();
		if (observer && intersectionObserverRef.current) observer.unobserve(intersectionObserverRef.current);
		scrollEventSubscriptionRef.current?.unsubscribe();
	}, [removeEventListener, observer]);

	const scrollHandler = useCallback((e) => {
		try {
			if (typeof observer === "boolean") {
				if (observer === false) {
					const elem = intersectionObserverRef;
					if (elem.current && elem.current.getBoundingClientRect().y + elem.current.getBoundingClientRect().height - 10 < window.innerHeight) {
						scrollEventSubscriptionRef.current?.unsubscribe();
						removeEventListener();
						callback();
					}
				}
			} else {
				if (intersectionObserverRef.current) observer?.observe(intersectionObserverRef.current);
				scrollEventSubscriptionRef.current?.unsubscribe();
				removeEventListener();
			}
		} catch (e) { console.error(e); }
	}, [observer, callback, removeEventListener]);

	useEffect(() => {
		if (!enable) return;

		if (observer && !scrollEventListenerAdded) {
			scrollEventSubscriptionRef.current = eventEmitterRef.current.subscribe("scroll", scrollHandler);
			setScrollEventListenerAdded(true);
			if ("ontouchstart" in window) {
				window.addEventListener("touchmove", handler.current);
			} else window.addEventListener('wheel', handler.current);
		}
		if (observer === false && !scrollEventListenerAdded) {
			scrollEventSubscriptionRef.current = eventEmitterRef.current.subscribe("scroll", scrollHandler);
			setScrollEventListenerAdded(true);
			if ("ontouchstart" in window) {
				window.addEventListener("touchmove", handler.current);
			} else {
				window.addEventListener('scroll', handler.current);
			}
		}
	}, [observer, scrollHandler, scrollEventListenerAdded, enable]);

	useUnmount(disable);

	return { intersectionObserverRef, disable };
}